import React, { useState, useEffect, memo, useCallback, useRef, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { sets, images } from '../../utils/api';

import GridView from './GridView';
import CarouselView from './CarouselView';
import TimelineView from './TimelineView';
import DetailsView from './DetailsView';
import CoinDetailModal from './CoinDetailModal';
import ImageViewerModal from '../shared/ImageViewerModal';
import SetHeader from './SetHeader';
import StatsBar from './StatsBar';
import { LoadingSpinner, ErrorMessage } from './utils';

const SetGallery = memo(({ 
  setId, 
  isShared = false, 
  onTagsChanged,
  onSetDeleted,
  initialViewMode = 'grid', 
  onViewModeChange, 
  sortPreferences: initialSortPreferences = {
    grid: { field: 'purchase_date', direction: 'desc' },
    carousel: { field: 'purchase_date', direction: 'desc' },
    timeline: { field: 'purchase_date', direction: 'desc' },
    details: { field: 'year', direction: 'asc' }
  }, 
  refreshTrigger,
  initialCoins,
  totalCoins: initialTotalCoins,
  setInfo: initialSetInfo
}) => {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [setInfo, setSetInfo] = useState(initialSetInfo || null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCoins, setTotalCoins] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 5 });
  const [currentSortPreferences, setCurrentSortPreferences] = useState(initialSortPreferences);
  const { user } = useAuth();
  
  const observerTarget = useRef(null);
  const carouselRef = useRef(null);
  const prevMouseX = useRef(null);
  const dragStartX = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const coinId = params.get('coin');
    const imageId = params.get('image');

    if (coinId && coins.length > 0) {
      const coin = coins.find(c => c.id === parseInt(coinId));
      if (coin) {
        setSelectedCoin(coin);
        if (imageId) {
          const imageIndex = coin.images.findIndex(img => img.id === parseInt(imageId));
          if (imageIndex !== -1) {
            setSelectedImage({
              index: imageIndex,
              images: coin.images,
              coin,
              isShared
            });
          }
        }
      }
    }
  }, [location.search, coins, isShared]);

  const handleCloseModal = useCallback(() => {
    setSelectedCoin(null);
    if (window.history.state?.modal === 'coin') {
      window.history.back();
    } else {
      const params = new URLSearchParams(location.search);
      params.delete('coin');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (selectedImage) {
          setSelectedImage(null);
          if (window.history.state?.modal === 'image') {
            window.history.back();
          }
        } else if (selectedCoin) {
          handleCloseModal();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCoin, selectedImage, handleCloseModal]);

  // Preload images function
  const preloadImage = useCallback((src, source) => {
    if (!src) return Promise.resolve();
    const processedSrc = images.getImage(src, source);
    if (loadedImages.has(processedSrc)) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, processedSrc]));
        resolve();
      };
      img.onerror = reject;
      img.src = processedSrc;
    });
  }, [loadedImages]);

  // Update visible range when scrolling
  const updateVisibleRange = useCallback(() => {
    if (!carouselRef.current) return;
    
    const container = carouselRef.current;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    
    const itemWidth = 500; // Approximate width of each item
    const start = Math.floor(scrollLeft / itemWidth);
    const visibleItems = Math.ceil(containerWidth / itemWidth);
    const end = start + visibleItems + 2;
    
    setVisibleRange({ start: Math.max(0, start - 2), end: end + 2 });
  }, []);

  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    dragStartX.current = e.clientX || e.touches?.[0]?.clientX;
    prevMouseX.current = dragStartX.current;
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDragging || !carouselRef.current) return;
    
    const currentX = e.clientX || e.touches?.[0]?.clientX;
    const deltaX = prevMouseX.current - currentX;
    
    if (deltaX !== 0) {
      carouselRef.current.scrollLeft += deltaX;
      prevMouseX.current = currentX;
    }
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (!carouselRef.current || !dragStartX.current) return;

    setIsDragging(false);
    
    // Snap to nearest item
    const itemWidth = 500;
    const currentScroll = carouselRef.current.scrollLeft;
    const targetScroll = Math.round(currentScroll / itemWidth) * itemWidth;
    
    carouselRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    dragStartX.current = null;
    prevMouseX.current = null;
  }, []);

  const scrollCarousel = useCallback((direction) => {
    if (!carouselRef.current) return;
    
    const itemWidth = 500;
    const currentScroll = carouselRef.current.scrollLeft;
    const targetScroll = direction === 'left' 
      ? Math.floor(currentScroll / itemWidth) * itemWidth - itemWidth
      : Math.ceil(currentScroll / itemWidth) * itemWidth + itemWidth;
    
    carouselRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, []);

  const sortCoins = useCallback((coinsToSort) => {
    if (!coinsToSort || !Array.isArray(coinsToSort)) return coinsToSort;
    
    const currentPrefs = currentSortPreferences?.[viewMode] || {
      field: 'purchase_date',
      direction: 'desc'
    };
    if (!currentPrefs.field) return coinsToSort;
    
    const { field, direction } = currentPrefs;
    
    return [...coinsToSort].sort((a, b) => {
      let aValue, bValue;
      
      switch (field) {
        case 'purchase_date':
          aValue = new Date(a.purchase_date || a.created_at).getTime();
          bValue = new Date(b.purchase_date || b.created_at).getTime();
          break;
        case 'year':
          const v1 = a.year;
          const v2 = b.year;

          if (!v1 && !v2) {
            aValue = 0;
            bValue = 0;
          } else if (!v1) {
            aValue = -1;
            bValue = 1;
          } else if (!v2) {
            aValue = 1;
            bValue = -1;
          } else {
            const hasNumbers = str => /\d/.test(str);
            if (hasNumbers(v1) && hasNumbers(v2)) {
              const getYear = str => {
                const match = str.match(/\d+/);
                return match ? parseInt(match[0], 10) : 0;
              };
              aValue = getYear(v1);
              bValue = getYear(v2);
            } else if (hasNumbers(v1)) {
              aValue = -1;
              bValue = 1;
            } else if (hasNumbers(v2)) {
              aValue = 1;
              bValue = -1;
            } else {
              aValue = v1.localeCompare(v2);
              bValue = 0;
            }
          }
          break;
        case 'grade':
          aValue = parseInt(a.grade?.match(/\d+/)?.[0]) || 0;
          bValue = parseInt(b.grade?.match(/\d+/)?.[0]) || 0;
          break;
        case 'denomination':
          aValue = a.denomination?.toLowerCase().trim() || '';
          bValue = b.denomination?.toLowerCase().trim() || '';
          break;
        default:
          aValue = a[field];
          bValue = b[field];
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });
  }, [currentSortPreferences, viewMode]);

  // Memoize sorted coins
  const sortedCoins = useMemo(() => sortCoins(coins), [coins, sortCoins]);

  const fetchSetCoins = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (!setId || typeof setId === 'undefined') {
        setError('No set selected');
        setLoading(false);
        return;
      }

      const data = await sets.get(setId);
      setSetInfo({
        ...data,
        coverImagePath: data.cover_image_path
      });
      setCoins(data.coins);
      setHasMore(false);
    } catch (err) {
      if (err.message === 'Tag set not found') {
        if (onSetDeleted) {
          onSetDeleted();
        }
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [setId, onSetDeleted]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    onViewModeChange?.(mode);
  }, [onViewModeChange]);

  // Update sort preferences when they change
  useEffect(() => {
    setCurrentSortPreferences(initialSortPreferences);
  }, [initialSortPreferences]);

  // Initialize data from props or fetch if needed
  useEffect(() => {
    if (initialCoins && initialSetInfo) {
      setCoins(initialCoins);
      setTotalCoins(initialTotalCoins || 0);
      setSetInfo(initialSetInfo);
      setCurrentSortPreferences(initialSortPreferences);
      setHasMore(false);
      setLoading(false);
      return;
    }

    const loadCoins = async () => {
      setLoading(true);
      setError(null);
      await fetchSetCoins();
    };

    loadCoins();
  }, [initialCoins, initialSetInfo, initialTotalCoins, initialSortPreferences, fetchSetCoins]);

  // Tag and cover image change handlers
  useEffect(() => {
    const handleTagChange = () => {
      fetchSetCoins();
      if (onTagsChanged) {
        onTagsChanged();
      }
    };

    const handleCoverChange = (event) => {
      // Just update the cover image path in setInfo without refetching everything
      setSetInfo(prev => ({
        ...prev,
        coverImagePath: event.detail
      }));
    };

    const handleSetChange = () => {
      // If set was deleted, navigate back to main page
      if (onSetDeleted) {
        onSetDeleted();
      }
    };

    window.addEventListener('tagChange', handleTagChange);
    window.addEventListener('coverChange', handleCoverChange);
    window.addEventListener('setChange', handleSetChange);
    return () => {
      window.removeEventListener('tagChange', handleTagChange);
      window.removeEventListener('coverChange', handleCoverChange);
      window.removeEventListener('setChange', handleSetChange);
    };
  }, [onTagsChanged, fetchSetCoins, onSetDeleted]);

  // Scroll event handler
  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateVisibleRange();
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [updateVisibleRange]);

  // Preload visible images
  useEffect(() => {
    if (!coins || viewMode !== 'carousel') return;

    const imagesToPreload = coins
      .slice(visibleRange.start, visibleRange.end)
      .flatMap(coin => coin.images || [])
      .map(image => image.file_path)
      .filter(path => !loadedImages.has(path));

    Promise.all(imagesToPreload.map(path => {
      const coin = coins.find(c => c.images?.some(img => img.file_path === path));
      const image = coin?.images?.find(img => img.file_path === path);
      return preloadImage(path, image?.source);
    }))
      .catch(() => {});
  }, [coins, visibleRange, preloadImage, viewMode, loadedImages]);

  const handleRemoveFromSet = useCallback(async (setId, coinId) => {
    try {
      await sets.removeCoins(setId, [coinId]);
      // Trigger a refresh of the set
      fetchSetCoins();
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('tagChange'));
    } catch (error) {
      // Silently handle error
    }
  }, [fetchSetCoins]);

  const handleCoinClick = useCallback((coin) => {
    setSelectedCoin(coin);
    const url = new URL(window.location);
    url.searchParams.set('coin', coin.id);
    window.history.pushState({ modal: 'coin' }, '', url);
  }, []);

  const handleImageLoad = useCallback((src) => {
    setLoadedImages(prev => new Set([...prev, src]));
  }, [setLoadedImages]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <SetHeader 
        setInfo={setInfo}
        isShared={isShared}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      <StatsBar 
        coinsShown={coins?.length ?? 0}
        totalCoins={totalCoins}
        isShared={isShared}
      />

      <div className="pt-4">
        <AnimatePresence mode="wait">
          {viewMode === 'grid' && (
            <GridView
              coins={sortedCoins}
              isShared={isShared}
              setInfo={setInfo}
              onCoinClick={handleCoinClick}
              loadingMore={loadingMore}
              hasMore={hasMore}
              observerTarget={observerTarget}
            />
          )}
          {viewMode === 'carousel' && (
            <CarouselView
              coins={sortedCoins}
              isShared={isShared}
              loadedImages={loadedImages}
              isDragging={isDragging}
              carouselRef={carouselRef}
              handleDragStart={handleDragStart}
              handleDragMove={handleDragMove}
              handleDragEnd={handleDragEnd}
              scrollCarousel={scrollCarousel}
              onCoinClick={handleCoinClick}
              onImageLoad={handleImageLoad}
            />
          )}
          {viewMode === 'timeline' && (
            <TimelineView
              coins={sortedCoins}
              isShared={isShared}
              onCoinClick={handleCoinClick}
              formatDate={(date, options) => formatDate(date, { ...options, user })}
            />
          )}
          {viewMode === 'details' && (
            <DetailsView
              coins={sortedCoins}
              isShared={isShared}
              setInfo={setInfo}
              onImageClick={(index, coin) => setSelectedImage({
                index,
                images: coin.images,
                coin,
                isShared
              })}
            />
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selectedCoin && (
          <CoinDetailModal
            coin={selectedCoin}
            isShared={isShared}
            setInfo={setInfo}
            onClose={handleCloseModal}
            onImageClick={(index) => {
              const image = selectedCoin.images[index];
              setSelectedImage({
                index,
                images: selectedCoin.images,
                coin: selectedCoin,
                isShared
              });
              const url = new URL(window.location);
              url.searchParams.set('image', image.id);
              window.history.pushState({ modal: 'image' }, '', url);
            }}
            onRemoveFromSet={handleRemoveFromSet}
            setId={setId}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedImage && (
          <ImageViewerModal
            selectedImage={selectedImage}
            onClose={() => {
              setSelectedImage(null);
              if (window.history.state?.modal === 'image') {
                window.history.back();
              }
            }}
            onNavigate={(newIndex) => {
              const image = selectedImage.images[newIndex];
              setSelectedImage({
                ...selectedImage,
                index: newIndex
              });
              const url = new URL(window.location);
              url.searchParams.set('image', image.id);
              window.history.replaceState({ modal: 'image' }, '', url);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

export default SetGallery;
